import { useEffect, useMemo, useState } from 'react';
import { ApiError, newIdempotencyKey, type ApiClient, type QuestionAnswerInput } from '../api/client';
import type { App, Definition, Draft, Problem, ProposalList, ReviewFinding, ReviewReport, StudioTarget } from '../api/types';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { usePlatform } from '../app/platform';
import type { Route, StudioRoute } from '../app/router';
import { toProblem, useAsync } from '../app/useAsync';
import { useDraftReview } from '../app/useDraftReview';
import { useDraftTrial } from '../app/useDraftTrial';
import { useIdempotencyKey } from '../app/useIdempotencyKey';
import { useStudioEvents } from '../app/useStudioEvents';
import { PublishDialog } from '../pages/PublishDialog';
import { Canvas } from './Canvas';
import { ChatPane } from './ChatPane';
import { NewMicroappDialog } from './NewMicroappDialog';
import { ProposalBar } from './ProposalBar';
import { ProposalDiffDialog } from './ProposalDiffDialog';
import { stageStartText, turnNotStartedText } from './StageAdvance';
import { StageBar } from './StageBar';
import { StudioShell } from './StudioShell';
import { useStudioSession } from './useStudioSession';

export interface StudioPageProps {
  route: StudioRoute;
  navigate(route: Route, options?: { replace?: boolean }): void;
  /** 渡したときだけヘッダーにログアウトを出す。 */
  onLogout?: () => void;
}

interface DraftApp {
  app: App;
  draft: Draft;
}

/** fixture には Studio の経路が無いので、導線も中身も出さない（C45）。 */
export function StudioPage(props: StudioPageProps) {
  const { mode } = usePlatform();
  if (mode === 'fixture') {
    return <EmptyState title="fixture モードでは Studio を使えません" description="実 API のモードで開いてください。" />;
  }
  return <StudioScreen {...props} />;
}

function StudioScreen({ route, navigate, onLogout }: StudioPageProps) {
  const { api, mode } = usePlatform();
  const { workspaceId } = route;
  const workspace = useAsync(() => api.getWorkspace(workspaceId), [api, workspaceId]);
  const drafts = useAsync(() => loadDraftApps(api, workspaceId), [api, workspaceId]);
  const session = useStudioSession({ api, workspaceId });
  // 新しい microapp のダイアログ（T28）。
  const [creatingApp, setCreatingApp] = useState(false);

  const entries = drafts.data ?? [];
  const selected = route.appId ? entries.find((entry) => entry.app.id === route.appId) : entries[0];
  const appId = selected?.app.id ?? null;

  // コード表示の保存の応答で受け取った Draft は取り直さずに使い、取り直しが届いたらそちらを正にする。
  const [draftOverride, setDraftOverride] = useState<Draft | null>(null);
  const [fetchedDrafts, setFetchedDrafts] = useState(drafts.data);
  if (drafts.data !== fetchedDrafts) {
    setFetchedDrafts(drafts.data);
    setDraftOverride(null);
  }
  const draft = draftOverride !== null && draftOverride.appId === appId ? draftOverride : (selected?.draft ?? null);
  // 選んでいる microapp の head のレビュー（D10）。版が進んだら（保存・反映・段階の変更）取り直す。
  const reviewState = useDraftReview({
    api,
    workspaceId,
    appId,
    enabled: mode === 'http' && draft !== null,
    reloadKey: draft?.headRevisionId,
  });

  // 「試す」→ 配備 → 試運転（G5）。画面を開いた時点の続きはレビューの trial から拾う。
  const trialState = useDraftTrial({
    api,
    workspaceId,
    appId,
    enabled: mode === 'http' && draft !== null,
    initial: reviewState.review?.trial ?? null,
    onChanged: () => {
      reviewState.reload();
      drafts.reload();
    },
  });

  // 配備が終わるまで Draft だけの app の getDefinition は 503 なので、ready になってから読む（G19）。
  const deployed =
    trialState.trial !== null && trialState.trial.status !== 'deploying' && trialState.trial.status !== 'deploy_failed';
  const definition = useAsync<Definition | null>(
    () => (appId !== null && deployed ? api.getDefinition(workspaceId, appId) : Promise.resolve(null)),
    [api, workspaceId, appId, deployed],
  );

  // 「公開するもの」は配備の describe が埋めた登録簿（build のレポート）から出す（G25）。
  const buildReportId = reviewState.review?.build.reportId ?? null;
  const buildReport = useAsync<ReviewReport | null>(
    () => (appId !== null && buildReportId !== null ? api.getDraftReport(workspaceId, appId, buildReportId) : Promise.resolve(null)),
    [api, workspaceId, appId, buildReportId],
  );
  const [publishing, setPublishing] = useState(false);

  const proposals = useAsync<ProposalList>(
    () => (appId ? api.listProposals(workspaceId, { appId, status: ['pending', 'interrupted'] }) : Promise.resolve({ items: [] })),
    [api, workspaceId, appId],
  );
  const proposal = proposals.data?.items[0] ?? null;
  const currentTurn = session.detail?.currentTurn ?? null;
  const running = currentTurn !== null && (currentTurn.status === 'queued' || currentTurn.status === 'running');
  // 公開前（動いているパッケージが none か trial）は、AI の変更がターンの終わりに自動で反映される（U8）。
  // レビューをまだ読めていない間は確認を挟む側（published 扱い）に倒す。
  const currentPackage = reviewState.review?.currentPackage ?? 'published';
  const autoApply = currentPackage === 'none' || currentPackage === 'trial';
  // 会話の行は app ごと。drafts の一覧に currentPackage は無いので activeUploadId で集める（→ 残課題 R34）。
  const autoApplyApps = useMemo(
    () => new Set(entries.filter((entry) => entry.draft.activeUploadId === null).map((entry) => entry.app.id)),
    [entries],
  );
  // 公開前でも、ターンの終わりを通って残った変更案（要約が付いている）は利用者が決められるようにする。
  const showProposal = proposal !== null && (!autoApply || (!running && proposal.summary !== null));
  // 帯の経過時間（U5）。まだ始まっていない（queued）ターンは受け付けた時刻から数える。
  const runningSince = running && currentTurn !== null ? (currentTurn.startedAt ?? currentTurn.createdAt) : null;

  // 文脈チップ。既定は選んでいる microapp（業務フローで要素を選んでいればその要素も）。× で Workspace 全体に戻し、
  // 別の microapp を選ぶかインスペクタの「この要素を対象にする」で戻る。
  const [targetScope, setTargetScope] = useState<'route' | 'workspace'>('route');
  const [scopedApp, setScopedApp] = useState(appId);
  if (scopedApp !== appId) {
    setScopedApp(appId);
    setTargetScope('route');
  }
  const target: StudioTarget =
    targetScope === 'workspace' || appId === null
      ? {}
      : { appId, ...(route.view === 'process' && route.element ? { elementId: route.element } : {}) };

  // 冪等キーは「同じ内容の押し直し」に同じものを使う（C37）。応答だけを落とした再送で指示が二重に積まれない。
  // 応答を待たずに SSE でも追えるが、押した直後の表示のために読み直す。
  const sendKey = useIdempotencyKey();
  const answerKey = useIdempotencyKey();
  // targetOverride はレビューの「AI に直させる」が指摘の要素とファイルを対象にするときだけ渡す（D30）。
  // attachments はチャットで置いた資料の itemId（U2）。空なら本文に載せない（冪等キーの指紋も変わらない）。
  const send = async (text: string, attachments: string[] = [], targetOverride?: StudioTarget) => {
    const sessionId = session.detail?.session.id ?? null;
    const body = { text, target: targetOverride ?? target, ...(attachments.length > 0 ? { attachments } : {}) };
    const idempotencyKey = sendKey.keyFor(JSON.stringify({ sessionId, ...body }));
    if (sessionId === null) await api.startSession(workspaceId, body, idempotencyKey);
    else await api.postTurn(workspaceId, sessionId, body, idempotencyKey);
    sendKey.done();
    session.reload();
  };
  const answer = async (questionId: string, answers: QuestionAnswerInput[]) => {
    if (session.detail === null) return;
    const idempotencyKey = answerKey.keyFor(JSON.stringify({ questionId, answers }));
    const accepted = await api.answerQuestion(workspaceId, session.detail.session.id, questionId, answers, idempotencyKey);
    answerKey.done();
    session.rememberQuestion(accepted.question);
    session.reload();
  };
  const cancel = async () => {
    if (session.detail === null) return;
    await api.cancelSession(workspaceId, session.detail.session.id, newIdempotencyKey());
    session.reload();
  };
  const resume = async () => {
    if (session.detail === null) return;
    await api.resumeSession(workspaceId, session.detail.session.id, newIdempotencyKey());
    session.reload();
  };

  const [showingDiff, setShowingDiff] = useState(false);
  // 反映と見送りは応答を待ってから、変更案・Draft（head と段階）・会話（proposal と status の行）を取り直す。
  const accept = async () => {
    if (proposal === null) return;
    await api.acceptProposal(workspaceId, proposal.id, newIdempotencyKey());
    setShowingDiff(false);
    proposals.reload();
    drafts.reload();
    session.reload();
  };
  const discard = async () => {
    if (proposal === null) return;
    await api.discardProposal(workspaceId, proposal.id, newIdempotencyKey());
    setShowingDiff(false);
    proposals.reload();
    session.reload();
  };

  // レビューの「AI に直させる」（D30）。作業中なら API が 429 turn_limit を返すので、チャットの上に出す。
  const [askProblem, setAskProblem] = useState<Problem | null>(null);
  const askFix = async (finding: ReviewFinding) => {
    if (appId === null) return;
    setAskProblem(null);
    try {
      await send(`レビューの指摘を直してください: [${finding.code}] ${finding.message}`, [], {
        appId,
        ...(finding.elementId ? { elementId: finding.elementId } : {}),
        ...(finding.path ? { path: finding.path } : {}),
      });
    } catch (error) {
      setAskProblem(toProblem(error));
    }
  };

  // run strip の「AI に原因を聞く」。対象は app だけ（配備の失敗に要素・パスは無い）。
  const askTrialFix = async (text: string) => {
    if (appId === null) return;
    setAskProblem(null);
    try {
      await send(text, [], { appId });
    } catch (error) {
      setAskProblem(toProblem(error));
    }
  };

  // 段階の移動（D12）。成功したら応答の Draft をそのまま使い、次の段階の条件を取り直す。
  const [stageBusy, setStageBusy] = useState(false);
  // 段階の移動＋固定文のターンの送信の間（U7）。stageBusy は移動の応答で戻るので、送信の間を止めるのはこちら。
  const [advancing, setAdvancing] = useState(false);
  const [stageProblem, setStageProblem] = useState<Problem | null>(null);
  /**
   * 段階を動かす本体（D12）。失敗は投げ直す。
   * 段階バーは moveStage が受けてポップオーバーに出し、チャットのアクション行（U7）は自分で受けて送信を止める。
   */
  const runStageMove = async (to: number, reason: string) => {
    if (draft === null) return;
    setStageBusy(true);
    setStageProblem(null);
    try {
      const mutation = await api.setDraftStage(workspaceId, draft.appId, { stage: to, reason }, draft.headRevisionId);
      setDraftOverride(mutation.draft);
      reviewState.reload();
    } catch (error) {
      const failure = toProblem(error);
      // 条件が足りない・版が進んだときは、手元の Draft と条件が古い。
      if (failure.code === 'stage_incomplete' || failure.code === 'revision_conflict') {
        drafts.reload();
        reviewState.reload();
      }
      throw error;
    } finally {
      setStageBusy(false);
    }
  };
  /** 段階バーの「次へ進める」「前の段階に戻る」（D12）。失敗はポップオーバーに出す。 */
  const moveStage = async (to: number, reason: string) => {
    try {
      await runStageMove(to, reason);
    } catch (error) {
      setStageProblem(toProblem(error));
    }
  };
  /**
   * チャットの「段階 n（段階名）へ進む」（U7）。段階を 1 つ進めてから、固定文のユーザーメッセージで
   * そのままターンを送る。段階の移動が失敗したらターンは送らない。送信だけが失敗したときは段階は進んだままで、
   * 「入力欄から指示を送ってください」に言い換えて投げ直す（失敗はアクション行が出す）。
   */
  const advanceStage = async () => {
    if (draft === null) return;
    const to = draft.stage + 1;
    setAdvancing(true);
    try {
      await runStageMove(to, '');
      try {
        // 対象は必ず動かした Draft の microapp（U7）。文脈チップを × で外していても、固定文のターンだけは
        // どの microapp の段階の話かを落とさない（askFix / askTrialFix と同じ扱い）。
        await send(stageStartText(to), [], { appId: draft.appId });
      } catch (error) {
        const failure = toProblem(error);
        throw new ApiError(
          { ...failure, code: 'turn_not_started', detail: turnNotStartedText(to, failure.detail) },
          `/v1/workspaces/${workspaceId}/sessions`,
        );
      }
    } finally {
      setAdvancing(false);
    }
  };

  // Workspace 全体を 1 本で購読する（C26）。cursor はセッションの snapshot のもので、取り直すと購読し直す。
  const streamState = useStudioEvents({
    api,
    workspaceId,
    // cursor は snapshot のものだけを使う（SSE で進む detail.eventCursor を渡すと 1 イベントごとに張り直しになる）。
    cursor: session.eventCursor,
    enabled: !session.loading,
    onEvent: (event) => {
      session.applyEvent(event);
      if (event.type === 'studio.proposal') proposals.reload();
      if (event.type === 'studio.turn' && (event.action === 'done' || event.action === 'failed' || event.action === 'cancelled' || event.action === 'waiting_answer')) {
        proposals.reload();
      }
      if (event.type === 'studio.revision') drafts.reload();
      if (event.type === 'studio.report' && event.appId === appId) reviewState.reload();
      // 配備の受付・run の開始・試運転の終わりと、公開（G30）。
      // 対象の試運転は SSE の trialId で読む（AI が作った新しい試運転も帯に出す。A2・G30）。
      if (event.type === 'studio.trial' && event.appId === appId) trialState.reload(event.trialId);
      if (event.type === 'studio.publication' && event.appId === appId) {
        reviewState.reload();
        drafts.reload();
      }
    },
  });
  useEffect(() => {
    // 保持期間を過ぎた cursor は再接続で回復しない。snapshot を取り直すと session.loading を経て購読し直す。
    if (streamState !== 'expired') return;
    session.reload();
    drafts.reload();
    proposals.reload();
    reviewState.reload();
    trialState.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState]);

  // ---- チャット ----
  const chat = (
    <div className="studio-chat-body">
      <StageBar
        stage={draft?.stage ?? null}
        conditions={reviewState.review?.conditions ?? []}
        // 固定文のターンを送っている間も段階バーから進められないようにする（段階と送った文がずれないため。U7）。
        busy={stageBusy || advancing}
        problem={stageProblem}
        waitingAnswer={session.detail?.session.status === 'waiting_answer'}
        // Draft が無ければ動かせない（表示だけ）。fixture モードはこの画面を描かない（C45）。
        {...(draft !== null
          ? {
              onAdvance: () => void moveStage(draft.stage + 1, ''),
              onBack: (reason: string) => void moveStage(draft.stage - 1, reason),
            }
          : {})}
      />
      {session.loading && session.detail === null && <Spinner label="会話を取得しています" />}
      <ProblemNotice problem={session.problem} action={<Button onClick={session.reload}>再取得</Button>} />
      <ProblemNotice
        problem={askProblem}
        tone={askProblem !== null && (askProblem.status === 409 || askProblem.status === 429) ? 'notice' : 'error'}
      />
      <ChatPane
        workspaceId={workspaceId}
        detail={session.detail}
        messages={session.messages}
        questions={session.questions}
        target={target}
        appName={selected?.app.name ?? null}
        onClearTarget={() => setTargetScope('workspace')}
        onSend={send}
        onAnswer={answer}
        onCancel={cancel}
        onResume={resume}
        stage={draft?.stage ?? null}
        stageConditions={reviewState.review?.conditions ?? []}
        stageBusy={stageBusy || advancing}
        proposalPending={proposal !== null}
        // Draft が無ければ動かせない。会話をまだ取っていない間（Spinner を出している間）も出さない（U7）。
        // fixture モードはこの画面を描かない（C45）。
        {...(draft !== null && !(session.loading && session.detail === null) ? { onAdvanceStage: advanceStage } : {})}
        autoApplyApps={autoApplyApps}
        onOpenHistory={(id) => navigate({ name: 'app', workspaceId, appId: id, tab: 'code', codePanel: 'history' })}
      />
      {/* 公開前は基盤が反映済みなので、変更案バーは出さない（U8）。 */}
      {showProposal && proposal !== null && (
        <ProposalBar
          proposal={proposal}
          running={running}
          onShowDiff={() => setShowingDiff(true)}
          onAccept={accept}
          onDiscard={discard}
        />
      )}
    </div>
  );
  // ---- チャットここまで ----

  return (
    <StudioShell
      workspaceId={workspaceId}
      workspaceName={workspace.data?.name ?? ''}
      navigate={navigate}
      chat={chat}
      onLogout={onLogout}
    >
      <ProblemNotice problem={drafts.problem} action={<Button onClick={drafts.reload}>再取得</Button>} />
      {drafts.loading && drafts.data === null ? (
        <Spinner label="microapp を取得しています" />
      ) : (
        <Canvas
          workspaceId={workspaceId}
          route={route}
          apps={entries.map((entry) => entry.app)}
          draft={draft}
          proposal={proposal}
          running={running}
          runningSince={runningSince}
          navigate={navigate}
          onNewMicroapp={() => setCreatingApp(true)}
          onTarget={(elementId) => {
            setTargetScope('route');
            navigate({ ...route, element: elementId }, { replace: true });
          }}
          onDraftChange={setDraftOverride}
          onReloadDraft={drafts.reload}
          review={reviewState.review}
          validating={reviewState.validating}
          validateProblem={reviewState.validateProblem ?? reviewState.problem}
          onValidate={() => void reviewState.validate()}
          onAskFix={(finding) => void askFix(finding)}
          trial={trialState.trial}
          trialDeploying={trialState.deploying}
          trialStarting={trialState.starting}
          trialProblem={trialState.problem ?? definition.problem}
          onTry={(confirmReplace) => void trialState.deploy(confirmReplace)}
          onStartTrial={(processKey, input) => void trialState.start(processKey, input)}
          onDismissTrial={trialState.dismiss}
          onReloadTrial={() => {
            // 帯の「再取得」は試運転と、配備した定義（開始フォームの元）の両方を読み直す（A4・A11）。
            trialState.reload();
            definition.reload();
          }}
          onPublish={() => setPublishing(true)}
          processes={definition.data?.processes ?? []}
          processesLoading={definition.loading}
          onAskTrialFix={(text) => void askTrialFix(text)}
        />
      )}
      {showProposal && proposal !== null && showingDiff && (
        <ProposalDiffDialog workspaceId={workspaceId} proposal={proposal} onClose={() => setShowingDiff(false)} />
      )}
      {creatingApp && (
        <NewMicroappDialog
          workspaceId={workspaceId}
          onCreated={(result) => {
            setCreatingApp(false);
            drafts.reload();
            navigate({ name: 'studio', workspaceId, appId: result.appId });
          }}
          onClose={() => setCreatingApp(false)}
        />
      )}
      {publishing && appId !== null && reviewState.review !== null && (
        <PublishDialog
          workspaceId={workspaceId}
          appId={appId}
          appName={selected?.app.name ?? appId}
          review={reviewState.review}
          // build のレポートが無ければ配備した定義の登録簿を使う（head を編集すると build は unchecked に戻る。T16-5）。
          declarations={buildReport.data?.declarations ?? definition.data?.declarations ?? null}
          onPublished={() => {
            setPublishing(false);
            reviewState.reload();
            drafts.reload();
          }}
          onClose={() => setPublishing(false)}
        />
      )}
    </StudioShell>
  );
}

/** Workspace の microapp のうち Draft を持つもの（GET A/draft の 404 は除く）。 */
async function loadDraftApps(api: ApiClient, workspaceId: string): Promise<DraftApp[]> {
  const page = await api.listApps(workspaceId, { limit: 100 });
  const loaded = await Promise.all(
    page.items.map(async (app) => {
      try {
        return { app, draft: await api.getDraft(workspaceId, app.id) };
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    }),
  );
  return loaded.filter((entry): entry is DraftApp => entry !== null);
}
